'use client';

import React from 'react';
import { VocabularyCard } from './VocabularyCard';
import { VocabularyCategory } from '@/lib/constants/vocabulary-categories';

// Skeleton component for loading state
const SkeletonCard = ({ index }: { index: number }) => (
  <div 
    className="p-3 sm:p-4 bg-muted/50 rounded-lg animate-pulse relative overflow-hidden"
    style={{ animationDelay: `${index * 0.1}s` }}
  >
    {/* Shimmer effect */}
    <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/10 to-transparent"></div>
    
    <div className="h-4 sm:h-5 bg-muted rounded w-2/3 mb-2"></div>
    <div className="h-3 sm:h-4 bg-muted rounded w-4/5 mb-2"></div>
    <div className="h-3 sm:h-4 bg-muted rounded w-1/3 mt-2"></div>
    <div className="absolute top-3 sm:top-4 right-3 sm:right-4 w-4 h-4 sm:w-5 sm:h-5 bg-muted rounded-full"></div>
  </div>
);

interface VocabularyCategorySectionProps {
  categories: VocabularyCategory[];
  isLoading: boolean;
  isSelected: (categoryId: string) => boolean;
  onCardClick: (categoryId: string) => void;
  onPreview?: (categoryId: string, categoryTitle: string) => void;
  skeletonCount?: number;
  error?: string | null;
  isCardLoading?: boolean;
}

export const VocabularyCategorySection: React.FC<VocabularyCategorySectionProps> = ({
  categories,
  isLoading,
  isSelected,
  onCardClick,
  onPreview,
  skeletonCount = 8,
  error,
  isCardLoading = false,
}) => {
  // Generate an array of skeletons with staggered animation
  const renderSkeletons = (count: number) => {
    return Array(count)
      .fill(0)
      .map((_, index) => <SkeletonCard key={`skeleton-${index}`} index={index} />);
  };

  return (
    <div>
      {/* Categories grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
        {error ? (
          // Show error state
          <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
            <div className="text-4xl mb-4">⚠️</div>
            <h3 className="text-lg font-medium text-foreground mb-2">Loading Error</h3>
            <p className="text-muted-foreground mb-4">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors"
            >
              Retry
            </button>
          </div>
        ) : isLoading ? (
          // Show skeleton cards while loading
          renderSkeletons(skeletonCount)
        ) : categories.length > 0 ? (
          // Show actual content when loaded with staggered animation
          categories.map((category, index) => (
            <div
              key={category.id}
              className="animate-in fade-in-0 slide-in-from-bottom-4 duration-300"
              style={{ animationDelay: `${index * 0.05}s` }}
            >
              <VocabularyCard
                category={category}
                isSelected={isSelected(category.id)}
                onClick={onCardClick}
                onPreview={onPreview}
                isLoading={isCardLoading}
              />
            </div>
          ))
        ) : (
          // No categories available
          <div className="col-span-full flex flex-col items-center justify-center py-12 text-center">
            <div className="text-4xl mb-4">📚</div>
            <h3 className="text-lg font-medium text-foreground mb-2">No vocabulary categories</h3>
            <p className="text-muted-foreground">No vocabulary options are available for this section</p>
          </div>
        )}
      </div>
    </div>
  );
};